import React, { useState, useEffect } from "react";

import moment from "moment";
import { DataGrid } from "@mui/x-data-grid";

import useAttendanceGridColumns from "./hooks/dataGridColumns";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { CustomGridToolbar } from "./hooks/GridToolbar";

import { useAttendances } from "api/attendances";
import { useGradeSections } from "api/grade_sections";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { tableColor } from "assets/css";
import { CustomButton } from "components/ui/buttons";

import { isTextEmpty } from "utils/commons";
import { useI18n } from "i18n";

const FORMAT_DATE_TIME = "YYYY-MM-DD HH:mm:ss";

type AttendanceColumnVisibilityModel = {
  id: boolean;
  name: boolean;
  stay: boolean;
  distance: boolean;
  battery: boolean;
  classroomNumber: boolean;
  firstReceiveTime: boolean;
  lastReceiveTime: boolean;
};

const DEFAULT_COLUMN_VISIBILITY_MODEL: AttendanceColumnVisibilityModel = {
  id: false,
  name: true,
  stay: true,
  distance: true,
  battery: true,
  classroomNumber: true,
  firstReceiveTime: false,
  lastReceiveTime: false,
};

const Page = () => {
  const { t } = useI18n();
  const gridColumnOptions = useAttendanceGridColumns();
  //----------------------------------------------------------------------
  //テーブルのカラムをデフォルトで表示を設定する処理
  let columnVisibilityModel = DEFAULT_COLUMN_VISIBILITY_MODEL;
  try {
    const savedVisibilityModel = localStorage.getItem(
      "attendancesVisibilityModel"
    );
    if (savedVisibilityModel) {
      const attendancesVisibilityModel: AttendanceColumnVisibilityModel =
        JSON.parse(savedVisibilityModel);

      columnVisibilityModel = attendancesVisibilityModel;
    }
  } catch (e) {}
  //----------------------------------------------------------------------

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { loading: loadingAttendances, getAttendances } = useAttendances();
  const { getGradeSections } = useGradeSections();

  const [updateIntervalId, setUpdateIntervalId] = useState<any>();

  const pageSize = 10;

  const [grades, setGrades] = useState([]);
  const [sections, setSections] = useState([]);
  const [attendances, setAttendances] = useState([]);

  const [grade, setGrade] = useState<string | undefined>(undefined);
  const [section, setSection] = useState<string | undefined>(undefined);

  const [updateTime, setUpdatetime] = useState<string>(
    moment().format(FORMAT_DATE_TIME)
  );

  //ハンドラー一覧
  const handleChangeGrade = (event: SelectChangeEvent) => {
    setGrade(event.target.value);
  };

  const handleChangeSection = (event: SelectChangeEvent) => {
    setSection(event.target.value);
  };

  const handleChangeColumnVisibilityModel = (newModel) => {
    localStorage.setItem(
      "attendancesVisibilityModel",
      JSON.stringify(newModel)
    );
  };

  //学年・組の一覧取得
  useEffect(() => {
    updateGradeSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //出欠状況を取得
  //学年・組を選択される際に、呼ばれるように実装
  useEffect(() => {
    //定期処理のIDが存在したら、定期処理を削除する。
    if (updateIntervalId) clearInterval(updateIntervalId);

    const selectedGrade = grade;
    const selectedSection = section;

    updateAttendances(selectedGrade, selectedSection);

    if (selectedGrade === undefined || selectedSection === undefined) return;

    //学年・組の情報を基づき、定期的に出欠状況を取得する。
    const updateAttendanceId = setInterval(() => {
      updateAttendances(selectedGrade, selectedSection);
    }, 1000 * 30);

    //定期処理のID登録
    setUpdateIntervalId(updateAttendanceId);

    //ページが終了する際に、定期処理を止める。
    window.addEventListener("beforeunload", (event) => {
      event.preventDefault();
      clearInterval(updateIntervalId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grade, section]);

  const updateNowTime = () => {
    const now = moment();
    const parseNow = now.format(FORMAT_DATE_TIME);

    setUpdatetime(parseNow);
  };

  const updateGradeSections = () => {
    getGradeSections()
      .then((responseResult) => {
        return responseResult["data"];
      })
      .then((responseData) => {
        const { result, grades, sections } = responseData;

        if (result === "success") {
          setGrades(grades);
          setSections(sections);
        }
      })
      .catch((err) => {});
  };

  const updateAttendances = (grade, section) => {
    if (grade === undefined || section === undefined) return;
    try {
      updateNowTime();
      getAttendances(grade["id"], section["id"])
        .then((responseResult) => {
          return responseResult["data"];
        })
        .then((responseData) => {
          const { result, data } = responseData;

          if (data != null) {
            data.forEach((item, index) => {
              item["id"] = index + 1;

              const firstName = item["firstName"]
              const lastName = item["lastName"]

              if (!isTextEmpty(firstName) && !isTextEmpty(lastName)) {
                item["name"] = `${firstName}, ${lastName}`;
              } else if (!isTextEmpty(firstName)) {
                item["name"] = firstName;
              } else if (!isTextEmpty(lastName)) {
                item["name"] = lastName;
              } else {
                item["name"] = t("attendance.emptyName");
              }
            });
          }

          if (result === "success") {
            const now = moment();
            const parseNow = now.format(FORMAT_DATE_TIME);
            setUpdatetime(parseNow);
            setAttendances(data);
          }
        })
        .catch((err) => {});
    } catch (e) {}
  };

  return (
    <React.Fragment>
      <Box>
        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center">
            <Typography
              variant="h1"
              gutterBottom
              sx={{ fontSize: "24px", fontWeight: "bold", mb: 4 }}
            >
              {t("attendance.title")}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="flex-end">
            <CustomButton
              onClick={() => {
                updateAttendances(grade, section);
              }}
            >
              {t("attendance.updateTime", { time: updateTime })}
            </CustomButton>
          </Stack>
          <Stack
            direction={isSmallScreen ? "column" : "row"}
            justifyContent="flex-end"
            spacing={2}
            mt={3}
            mb={3}
          >
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="demo-simple-select-label">
                {t("field.grade")}
              </InputLabel>
              <Select
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                value={grade}
                autoWidth
                displayEmpty
                onChange={handleChangeGrade}
                label={t("field.grade")}
              >
                {grades.map((item) => (
                  <MenuItem id={item["id"]} key={item["id"]} value={item}>
                    {item["name"]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="demo-simple-select-label">
                {t("field.section")}
              </InputLabel>
              <Select
                autoWidth
                displayEmpty
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                value={section}
                onChange={handleChangeSection}
                label={t("field.section")}
              >
                {sections.map((item) => (
                  <MenuItem id={item["id"]} key={item["id"]} value={item}>
                    {item["name"]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <DataGrid
            loading={loadingAttendances}
            rows={attendances}
            rowCount={attendances["length"]}
            columns={gridColumnOptions}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: pageSize,
                },
              },
              columns: {
                columnVisibilityModel: columnVisibilityModel,
              },
            }}
            disableRowSelectionOnClick
            sx={{
              ...tableColor,
            }}
            onColumnVisibilityModelChange={handleChangeColumnVisibilityModel}
            slots={{
              toolbar: CustomGridToolbar,
            }}
          />
        </Container>
      </Box>
    </React.Fragment>
  );
};

export default Page;
